import api from "./api";

/**
 * The platform's public content pages (Terms, Privacy, …) — `GET /pages/{slug}`.
 *
 * Public: a business has to be able to read the terms before agreeing to
 * them, so this works signed out. The title and body come back in the
 * `Accept-Language` asked for, falling back to the other language when one is
 * empty; `locale` says which one was actually returned.
 *
 * `body` is HTML the server sanitised when the admin saved it (an allow-list:
 * no scripts, iframes, event handlers or unsafe links), which is why the modal
 * renders it directly and there is no second sanitiser here — the server's is
 * the one every client is held to.
 *
 * Each page is cached per language for the session (until a reload), so
 * opening the terms twice is one request. A 404 — the page is unpublished — is
 * cached as `null` too; a network or server error is not, so Retry asks again.
 */
const cache = new Map();

const keyFor = (slug, language) => `${slug}:${language}`;

/** The cached answer, if one has arrived: a page, `null` (unpublished), or `undefined`. */
export const peekContentPage = (slug, language) => {
  const entry = cache.get(keyFor(slug, language));
  return entry?.done ? entry.value : undefined;
};

/** `{ slug, title, body, meta_description, locale, updated_at }`, or `null` when unpublished. */
export const fetchContentPage = (slug, language) => {
  const key = keyFor(slug, language);
  const cached = cache.get(key);
  if (cached) return cached.promise;

  const entry = { done: false, value: undefined, promise: null };

  entry.promise = api
    .get(`/pages/${encodeURIComponent(slug)}`, { headers: { "Accept-Language": language } })
    .then(({ data }) => data?.data?.page ?? null)
    .catch((err) => {
      if (err.response?.status === 404) return null;
      // Not cached: the next open (or Retry) tries again.
      cache.delete(key);
      throw err;
    })
    .then((value) => {
      entry.done = true;
      entry.value = value;
      return value;
    });

  cache.set(key, entry);
  return entry.promise;
};
